// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/ProductDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProductDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = data?.name || data?.tradeName || data?.ean || data?.brand || data?.description || data?.shortDescription;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['product-data']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'product-data',
          label: (
            <Flex align="center" gap={8}>
              <ShoppingOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('product_data')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {data.name && (
                <Descriptions.Item label={translateX('name')}>
                  {renderValue(data.name)}
                </Descriptions.Item>
              )}
              {data.shortDescription && (
                <Descriptions.Item label={translateX('short_description')}>
                  {renderValue(data.shortDescription)}
                </Descriptions.Item>
              )}
              {data.tradeName && (
                <Descriptions.Item label={translateX('trade_name')}>
                  {renderValue(data.tradeName)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="EAN/GTIN">
                {renderValue(data.ean) || renderValue(data.gtin) || 'N/A'}
              </Descriptions.Item>
              {data.manufacturerCode && (
                <Descriptions.Item label={translateX('manufacturer_code')}>
                  {renderValue(data.manufacturerCode)}
                </Descriptions.Item>
              )}
              {data.brand && (
                <Descriptions.Item label={translateX('brand')}>
                  {renderValue(data.brand)}
                </Descriptions.Item>
              )}
              {data.description && (
                <Descriptions.Item label={translateX('description')}>
                  {renderValue(data.description)}
                </Descriptions.Item>
              )}
              {data.notes && (
                <Descriptions.Item label={translateX('notes')}>
                  {renderValue(data.notes)}
                </Descriptions.Item>
              )}
              {data.searchKeywords?.length > 0 && (
                <Descriptions.Item label={translateX('search_keywords')}>
                  <Flex wrap="wrap" gap={4}>
                    {data.searchKeywords.map((kw, idx) => (
                      <Tag key={idx}>{renderValue(kw)}</Tag>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProductDataSection;
